<template>
  <div class="bbp-financial">
    <h2 class="bbp-title-page">Financial planning</h2>
    <div class="bbp-financial-header">
      <div class="bbp-financial-header-totals">
        <BBPTotalCard v-for="total in projectsTotals"
                      :key="total.id"
                      :item="total"
                      :active="total.active"
                      :title="total.title"
                      :end-val="total.value"
                      @setActive="setFilterByStatus"/>
      </div>
      <div class="bbp-financial-header-dates">
        <BBPDatePicker :input="datePickerInput"
                       :model="datePickerModel"
                       @input="setFilterDates"
                       styleType="table-filter"/>
      </div>
    </div>
    <div class="bbp-financial-body">
      <BBPTable :columns="columns"
                :rows="fullProjectsList"
                :limit="defLimit"
                :page="currentPage"
                :no-data="noData"
                :max-height="`calc(100vh - ${fullInfo.totalPages > 1 ? 243 : 176}px)`"
                @sort="setSort"/>
      <BBPPagination v-show="fullProjectsList.length > 0"
                     :data="fullInfo"
                     :getQuery="getAllProjects"
                     :limit="defLimit"
                     :sticky="true"
                     class="bbp-pagination"/>
    </div>
    <transition name="bbp-fade" appear>
      <BBPLoader v-if="loaderRun"></BBPLoader>
    </transition>
  </div>
</template>

<script>
import financialPlanningColumns from '@/constants/TablesSettings/listOfFinancialPlanning'
import BBPTable from '@/components/Main/BBPTable/BBPTable'
import BBPPagination from '@/components/Main/BBPPagination/BBPPagination'
import BBPLoader from '@/components/Main/BBPLoader/BBPLoader'
import ProjectsApi from '@/api/Projects/api'
import setProjectFullInfo from '@/mixin/setProjectFullInfo'
import {find, get, forEach} from 'lodash'
import projectStatuses from '@/constants/projectStatuses'
import BBPDatePicker from '@/components/Main/BBPForm/BBPDatePicker/BBPDatePicker'
import moment from 'moment'
import BBPTotalCard from '@/components/Main/BBPTotalCard/BBPTotalCard'

export default {
  name: 'TotalListOfProjects',
  mixins: [setProjectFullInfo],
  components: {
    BBPTotalCard,
    BBPDatePicker,
    BBPTable,
    BBPPagination,
    BBPLoader
  },
  data () {
    return {
      columns: financialPlanningColumns,
      fullInfo: {},
      defLimit: 50,
      loaderRun: false,
      currentPage: 1,
      noData: false,
      sort: 'dateStart&sort[]=-1',
      datePickerInput: { model: 'dates', mode: 'range', format: 'DD MMM, YYYY' },
      datePickerModel: {
        dates: {
          start: moment().subtract(10, 'years').format(),
          end: moment().add(10, 'years').format()
        }
      },
      projectsTotals: [
        {id: 'totalDelayedStatus', title: 'Delayed', value: 0, status: 2, active: false},
        {id: 'totalInProgressStatus', title: 'In Progress', value: 0, status: 1, active: false},
        {id: 'totalCompletedStatus', title: 'Completed', value: 0, status: 3, active: false},
        {id: 'totalLostStatus', title: 'Lost', value: 0, status: 4, active: false}
      ],
      filterByDate: ''
    }
  },
  computed: {
    fullProjectsList: {
      get () {
        return this.$store.getters.fullProjectsList
      },
      set (data) {
        this.$store.dispatch('setFullProjectsList', data)
      }
    },
    currentDate () {
      return {
        start: moment().subtract(50, 'year').format(),
        end: moment().add(50, 'year').format()
      }
    },
    projectsTotalsDefault () {
      const statuses = projectStatuses.map(({id}) => id)
      return statuses.join('&status[]=')
    },
    filterByStatus () {
      const activeStatus = this.projectsTotals.filter(item => item.active)
      const statuses = activeStatus.map(item => item.status)
      return statuses.join('&status[]=')
    }
  },
  methods: {
    setSort (value) {
      this.sort = value
      this.getAllProjects({})
    },
    getProjectsTotals () {
      ProjectsApi.getProjectsTotals({
        dateStart: this.filterByDate,
        status: this.filterByStatus || this.projectsTotalsDefault,
        projectType: '1&projectType[]=2'
      })
        .then(resp => {
          forEach(resp.data, (value, key) => {
            const target = find(this.projectsTotals, {id: key})
            if (target) target.value = value
          })
        })
        .catch(err => {
          console.error(err)
        })
    },
    getAllProjects ({page}) {
      this.getProjectsTotals()
      this.loaderRun = true
      this.currentPage = page || this.currentPage
      ProjectsApi.getProjects({
        limit: this.defLimit,
        page: this.currentPage,
        sort: this.sort,
        status: this.filterByStatus || this.projectsTotalsDefault,
        dateStart: this.filterByDate,
        projectType: '1&projectType[]=2'
      })
        .then(async resp => {
          this.noData = !resp.data.totalDocs
          this.fullInfo = resp.data
          this.fullProjectsList = await this.getProjectInfo(resp.data.projects)
          this.loaderRun = false
        })
        .catch(err => {
          this.noData = true
          console.error(err)
          this.loaderRun = false
        })
    },
    async getProjectInfo (requests) {
      const promises = requests.map(async request => {
        const result = await this.setProjectFullInfo(request, true)
        result.status = find(projectStatuses, {id: result.status})
        return result
      })
      const result = await Promise.all(promises)
      return result
    },
    setFilterByStatus (item) {
      const val = get(item, 'active', false)
      item.active = !val
      this.getAllProjects({})
    },
    setFilterDates (date) {
      const startDate = get(date, 'start', '')
      const endDate = get(date, 'end', '')
      const format = 'YYYY-MM-DD'
      this.filterByDate = `${moment(startDate).format(format)};;${moment(endDate).format(format)}`
      this.getAllProjects({})
    }
  },
  beforeMount () {
    this.setFilterDates(this.currentDate)
    this.getAllProjects({})
  }
}
</script>

<style lang="scss" scoped>
  @import "../../sass/variables";
  @import "../../sass/mixins";

  .bbp-financial {
    @include global-font;

    &-header {
      display: grid;
      grid-template-columns: auto 223px;
      margin: 12px 0;

      &-totals {
        display: flex;
        align-items: center;
      }

      &-dates {
        display: grid;
        height: 42px;
        font-size: 12px;
        line-height: 14px;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/TotalListOfProjects/TotalListOfProjects.vue